/**
 * The offline outbox.
 *
 * Ori is built for clinics with intermittent power and phones on patchy mobile
 * networks. A reading taken in a ward with no signal is not less valuable than
 * one taken on office wifi — it is more valuable, because it comes from the
 * kind of place the published datasets never reached. Losing it because a POST
 * failed would be a quiet, ongoing act of exclusion.
 *
 * So a donated reading is written to durable storage *first*, and sent second.
 * The queue survives a reload, a crash, a flat battery and a closed tab.
 *
 * One decision here is worth defending, because the obvious alternative is
 * wrong: readings are queued UNSIGNED and signed at the moment they are sent.
 *
 * The server rejects any signed payload whose `issuedAt` is stale — that is what
 * stops a captured request being replayed months later. A reading signed on
 * Tuesday and delivered on Friday would be indistinguishable from exactly that
 * attack, and would be refused. Signing at send time keeps the freshness window
 * meaningful. Nothing is lost by it: the measurement's own timestamp travels in
 * `observedAt` inside the signed body, so the corpus still records when the
 * reading was actually taken, and the signature still covers it.
 */

const DB_NAME = 'omnialera-ori-outbox';
const DB_VERSION = 1;
const STORE = 'queued';

/** Give up on a reading the server has definitively refused. */
const MAX_ATTEMPTS = 8;

/** Readings older than this are dropped: a year-old queue is a bug, not a backlog. */
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

function openDb() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id', autoIncrement: true });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function run(db, mode, fn) {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, mode);
    const req = fn(tx.objectStore(STORE));
    tx.onerror = () => reject(tx.error);
    if (req) {
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    } else {
      tx.oncomplete = () => resolve();
    }
  });
}

const available = () => typeof indexedDB !== 'undefined';

/** Put a reading in the queue. Returns the queue entry id, or null if unavailable. */
export async function enqueue(reading) {
  if (!available()) return null;
  const db = await openDb();
  try {
    return await run(db, 'readwrite', (store) =>
      store.add({ reading, queuedAt: Date.now(), attempts: 0, lastError: null }),
    );
  } finally {
    db.close();
  }
}

export async function pending() {
  if (!available()) return [];
  const db = await openDb();
  try {
    return (await run(db, 'readonly', (store) => store.getAll())) ?? [];
  } finally {
    db.close();
  }
}

export async function count() {
  if (!available()) return 0;
  const db = await openDb();
  try {
    return (await run(db, 'readonly', (store) => store.count())) ?? 0;
  } finally {
    db.close();
  }
}

async function drop(id) {
  const db = await openDb();
  try {
    await run(db, 'readwrite', (store) => store.delete(id));
  } finally {
    db.close();
  }
}

async function markFailed(entry, message) {
  const db = await openDb();
  try {
    await run(db, 'readwrite', (store) =>
      store.put({ ...entry, attempts: entry.attempts + 1, lastError: message }),
    );
  } finally {
    db.close();
  }
}

/**
 * Should this failure cost the reading its place in the queue?
 *
 * A 4xx that is not a rate limit means the server has looked at this reading
 * and will never accept it — retrying forever would be a loop, not resilience.
 * Anything else (offline, 5xx, 429) is temporary and the reading waits.
 */
function permanent(err) {
  const status = err?.status ?? 0;
  if (status === 429) return false;
  return status >= 400 && status < 500;
}

/**
 * Try to deliver everything queued.
 *
 * @param {(reading: object) => Promise<object>} send  usually `api.contribute`
 * @returns {Promise<{sent: number, failed: number, dropped: number, remaining: number}>}
 */
let inFlight = null;

export async function flush(send) {
  // One run at a time, process-wide.
  //
  // flush() is called after a scan AND from the connectivity watcher, which
  // wakes on `online` and on every return to visibility. On a slow connection —
  // the intended environment — a reading whose POST is still in flight when the
  // phone wakes was sent a second time, because an entry is only dropped after
  // its POST resolves. The server cannot catch it: each send mints a fresh
  // nonce, so both requests are legitimately distinct, and the corpus counted
  // the reading twice.
  //
  // Concurrent callers share the run rather than starting their own. Anything
  // queued after this run began is picked up by the next one.
  if (inFlight) return inFlight;
  inFlight = run_(send).finally(() => {
    inFlight = null;
  });
  return inFlight;
}

async function run_(send) {
  if (!available()) return { sent: 0, failed: 0, dropped: 0, remaining: 0, sentKeys: [] };

  const entries = await pending();
  let sent = 0;
  let failed = 0;
  let dropped = 0;
  // Which readings actually reached the corpus, so the caller can mark exactly
  // those and no others. Totals alone cannot say which.
  const sentKeys = [];

  for (const entry of entries) {
    if (Date.now() - entry.queuedAt > MAX_AGE_MS || entry.attempts >= MAX_ATTEMPTS) {
      await drop(entry.id);
      dropped += 1;
      continue;
    }
    try {
      await send(entry.reading);
      await drop(entry.id);
      sent += 1;
      if (entry.reading?.observedAt) sentKeys.push(entry.reading.observedAt);
    } catch (err) {
      if (permanent(err)) {
        await drop(entry.id);
        dropped += 1;
      } else {
        await markFailed(entry, err?.message ?? 'unknown');
        failed += 1;
        // The network is down or the server is unwell. Trying the rest of the
        // queue right now would just be the same failure, several more times.
        break;
      }
    }
  }

  return { sent, failed, dropped, remaining: await count(), sentKeys };
}

/** Empty the queue. Used by "delete everything", where the queue is data too. */
export async function clear() {
  if (!available()) return;
  const db = await openDb();
  try {
    await run(db, 'readwrite', (store) => store.clear());
  } finally {
    db.close();
  }
}

/**
 * Call `onOnline` whenever the device looks reachable again.
 * `online` fires on reconnection; the visibility check catches the far more
 * common case of a phone that was simply asleep in someone's pocket.
 */
export function watchConnectivity(onOnline) {
  if (typeof window === 'undefined') return () => {};
  const wake = () => {
    if (navigator.onLine !== false) onOnline();
  };
  // Named, not an inline closure: an anonymous listener cannot be removed, and
  // this one was outliving every unmount — so a remount left two handlers, then
  // three, each firing its own flush on the next wake.
  const onVisible = () => {
    if (document.visibilityState === 'visible') wake();
  };
  window.addEventListener('online', wake);
  document.addEventListener('visibilitychange', onVisible);
  return () => {
    window.removeEventListener('online', wake);
    document.removeEventListener('visibilitychange', onVisible);
  };
}
